import type { InsightAgentLogEntry } from '@innermate/shared';
import { KNOWN_AGENT_ERROR_TYPES } from '@innermate/shared';
/**
 * 어드민 채팅 로그 — Insight Agent 로그 entry 의 상태 분류 + UI 매핑.
 *
 * 상태는 entry 의 별도 필드가 아니라 `errorType` + `cacheTimestamp` 조합으로 derive.
 * - errorType === null && cacheTimestamp === null    → '정상실행'
 * - errorType === null && cacheTimestamp !== null    → '캐시 적용됨'
 * - errorType 이 KNOWN_ERROR_TYPES 중 하나            → 그 값
 * - 그 외 정의되지 않은 errorType                     → '알 수 없음'
 *
 * 9종 status 각자 lucide 아이콘 + severity 색 클래스 + 사용자용 설명.
 */
import {
  BotMessageSquare,
  CircleCheck,
  type LucideIcon,
  MessageCircleCode,
  MessageCircleDashed,
  MessageCircleMore,
  MessageCircleQuestion,
  MessageCircleWarning,
  MessageCircleX,
  Zap,
} from 'lucide-react';

export type AgentLogStatus =
  | '정상실행'
  | '캐시 적용됨'
  | '범위 외 질문'
  | '데이터없음'
  | '미완료'
  | '미학습'
  | '유효성미달'
  | '서버에러'
  | '알 수 없음';

export type AgentLogSeverity = 'success' | 'warning' | 'error' | 'muted';

interface StatusMeta {
  icon: LucideIcon;
  severity: AgentLogSeverity;
  description: string;
}

/**
 * 9종 status → 메타 매핑.
 * 아이콘은 lucide-react 의 컴포넌트. severity 는 색 클래스 선택에 사용 (`SEVERITY_CLASSES`).
 */
export const AGENT_LOG_STATUS_META: Record<AgentLogStatus, StatusMeta> = {
  정상실행: {
    // 원안: message-circle-check. lucide-react 현재 버전에 없어 CircleCheck 로 대체.
    icon: CircleCheck,
    severity: 'success',
    description: '특별한 문제 없이 정상적으로 답변이 제공된 경우 표시됩니다.',
  },
  '캐시 적용됨': {
    icon: Zap,
    severity: 'success',
    description:
      '누군가 특정 시간 내에 동일한 질문을 한 경우, 캐싱한 답변을 즉각 제공합니다. 이 아이콘으로 표기된 질문은 캐싱된 답변이 제공되었다는 의미입니다.',
  },
  '범위 외 질문': {
    icon: MessageCircleCode,
    severity: 'warning',
    description:
      '에이전트에서 정의한 목적이나 서비스 범위를 벗어난 주제에 대한 질문을 의미합니다. 해당 질문은 모델의 설계 의도와 무관하여 응답 대상에 포함되지 않습니다.',
  },
  데이터없음: {
    icon: MessageCircleX,
    severity: 'warning',
    description: '사용자가 질문한 내용 중 현재 데이터가 존재하지 않는 경우에 표시됩니다.',
  },
  미완료: {
    icon: MessageCircleDashed,
    severity: 'warning',
    description:
      '사용자가 최종 답변을 받기 전 화면을 떠나 마지막 프로세스까지 도달하지 않은 경우에 표시됩니다.',
  },
  미학습: {
    // 원안: message-circle-question-mark. 현재 lucide-react 에 없어 MessageCircleQuestion 으로 대체.
    icon: MessageCircleQuestion,
    severity: 'warning',
    description:
      '에이전트가 다루는 주제 범위에는 포함되지만, 학습 데이터에 반영되지 않아 충분한 지식을 확보하지 못한 질문을 의미합니다. 따라서 관련성은 있으나 현재 기준으로는 정확한 답변이 어려운 상태입니다.',
  },
  유효성미달: {
    icon: MessageCircleMore,
    severity: 'warning',
    description:
      '입력된 질문이 특정 주제 또는 유형에 따라 요구되는 검증 기준을 충족하지 못한 상태를 의미합니다. 이로 인해 후속 처리 단계로 진행되지 못하며, 추가적인 조건 충족 또는 입력 보완이 필요한 상황입니다.',
  },
  서버에러: {
    icon: MessageCircleWarning,
    severity: 'error',
    description: 'AI 로직 또는 웹서비스에서 오류가 발생한 경우에 표시됩니다.',
  },
  '알 수 없음': {
    icon: BotMessageSquare,
    severity: 'muted',
    description:
      '사전에 정의되지 않은 상태인 경우 표시됩니다. 오류로 인해 코드 자체가 노출되는 것을 방지하기 위해 마련해두었습니다.',
  },
};

/** Tailwind 색 클래스 — 칩/배지 styling 용. */
export const SEVERITY_CLASSES: Record<AgentLogSeverity, string> = {
  success: 'text-emerald-600 bg-emerald-500/10 border-emerald-400/30',
  warning: 'text-amber-600 bg-amber-500/10 border-amber-400/30',
  error: 'text-red-600 bg-red-500/10 border-red-400/30',
  muted: 'text-foreground-muted bg-surface-muted border-border',
};

/**
 * 아이콘 / 텍스트 색만 필요할 때 — literal 클래스 매핑.
 * (동적 보간 `text-${...}` 은 Tailwind JIT 가 탐지 못 함. literal 사용 강제.)
 */
export const SEVERITY_TEXT_CLASS: Record<AgentLogSeverity, string> = {
  success: 'text-emerald-600',
  warning: 'text-amber-600',
  error: 'text-red-600',
  muted: 'text-foreground-muted',
};

/** 타임라인 막대 배경 색 — literal 매핑. */
export const SEVERITY_BAR_CLASS: Record<AgentLogSeverity, string> = {
  success: 'bg-emerald-500/70',
  warning: 'bg-amber-500/70',
  error: 'bg-red-500/80',
  muted: 'bg-foreground-muted/40',
};

/**
 * entry 한 줄의 status derive. error_type + cacheTimestamp 조합 기반.
 */
export function deriveEntryStatus(entry: InsightAgentLogEntry): AgentLogStatus {
  if (entry.errorType === null) {
    return entry.cacheTimestamp !== null ? '캐시 적용됨' : '정상실행';
  }
  return (KNOWN_AGENT_ERROR_TYPES as readonly string[]).includes(entry.errorType)
    ? (entry.errorType as AgentLogStatus)
    : '알 수 없음';
}

/**
 * 여러 entry 의 overall status — severity priority + 동률이면 가장 최근 (end_timestamp 기준).
 * 빈 배열은 null.
 *
 * 우선순위:
 *  1. 서버에러     → error
 *  2. 미완료/유효성미달/미학습/데이터없음/범위 외 질문 → warning (가장 최근)
 *  3. 알 수 없음   → muted
 *  4. 정상실행/캐시 적용됨 → success (캐시 적용됨 우선 — 있으면)
 */
export function deriveOverallStatus(entries: InsightAgentLogEntry[]): AgentLogStatus | null {
  if (entries.length === 0) return null;

  const labelled = entries.map((e) => ({ status: deriveEntryStatus(e), end: e.endTimestamp }));

  // priority 1: 서버에러
  if (labelled.some((x) => x.status === '서버에러')) return '서버에러';

  // priority 2: warning 7종 (미완료 등) — 가장 최근 end_timestamp
  const warnings: AgentLogStatus[] = [
    '미완료',
    '유효성미달',
    '미학습',
    '데이터없음',
    '범위 외 질문',
  ];
  const warningEntries = labelled.filter((x) => (warnings as readonly string[]).includes(x.status));
  if (warningEntries.length > 0) {
    warningEntries.sort((a, b) => (a.end < b.end ? 1 : a.end > b.end ? -1 : 0));
    // biome-ignore lint/style/noNonNullAssertion: length > 0 직전 체크로 [0] 보장
    return warningEntries[0]!.status;
  }

  // priority 3: 알 수 없음
  if (labelled.some((x) => x.status === '알 수 없음')) return '알 수 없음';

  // priority 4: success — 캐시 적용됨 우선
  if (labelled.some((x) => x.status === '캐시 적용됨')) return '캐시 적용됨';
  if (labelled.some((x) => x.status === '정상실행')) return '정상실행';

  return '알 수 없음';
}
